import axios, { AxiosResponse } from 'axios'
import aqe from '@pushly/aqe'
import { Singleton } from 'typescript-ioc/es5'
import { IUserPermissionsResponse } from '../interfaces/user-permissions'
import AccountUserModel from '../models/access-control/account-user.model'
import DomainUserModel from '../models/access-control/domain-user.model'
import { IApiCallOptions, IApiResponse } from '@pushly/aqe/lib/interfaces'
import { AppService } from './app.service'
import { Container } from 'typescript-ioc/es5'
import { ApiCallOptions } from '@pushly/aquarium-utils/lib/react/index'
import querystring from 'query-string'
import { axiosFetch, isAxiosCancellation, isAxiosResponseError } from '../_utils/axios'
import { simpleNotification } from '../_utils/notifications'
import { IAccessPolicy } from '../interfaces/access-policy'

export interface SerializedRule {
    action: string | string[]
    subject: string | string[]
    conditions?: Record<string, any>
    inverted?: boolean
}

export interface IPermissionsChangeSet<T> {
    dirty: T
    clean?: T
}

export interface IRemovedAccessRow {
    id: number
    role_id: number
}

export interface IRemovedAccountRow extends IRemovedAccessRow {
    account_id: number
}

export interface IRemovedDomainRow extends IRemovedAccessRow {
    domain_id: number
}

export interface IRemovedAccountAccess {
    account: IRemovedAccountRow
    domains: IRemovedDomainRow[]
}

/**
 * The server answers a missing row and a refusal with the same 403, so they share one failure.
 * A 503 whose body names ACCESS_CACHE_STALE means the row is gone but its cached copy could not
 * be refreshed, which is a success the caller must still qualify. Any other 503 says nothing
 * about the delete, so it is a failure.
 */
export type AccessResetFailure = 'not_network_scope' | 'refused' | 'failed'

export type AccessResetResult<T> =
    | { ok: true; removed?: T; cacheStale: boolean }
    | { ok: false; failure: AccessResetFailure }

const emptyPermissionsResponse: IUserPermissionsResponse = {
    accounts: [],
    domains: [],
}

@Singleton
class PermissionsService {
    protected readonly appService: AppService

    public constructor() {
        this.appService = Container.get(AppService)
    }

    public async loadUserPermissions(id: number): Promise<IApiResponse<IUserPermissionsResponse>> {
        let ok = false
        let data: IUserPermissionsResponse = emptyPermissionsResponse
        let meta: any = {}
        let cancelled: boolean = false
        let error: Error | undefined

        try {
            const res = await axios.get(`${aqe.defaults.publicApiDomain}/v2/users/${id}/permissions`)

            data = {
                accounts: res.data.data?.accounts ?? emptyPermissionsResponse.accounts,
                domains: res.data.data?.domains ?? emptyPermissionsResponse.domains,
            }
            ok = true
        } catch (err) {
            if (process.env.IS_LOCAL) {
                console.debug('permissions_error', err)
            }

            error = err
        }

        return { ok, data, meta, error, cancelled }
    }

    public async updateAccountUserPermissions(
        id: number,
        changeRecords: Array<IPermissionsChangeSet<AccountUserModel>>,
        opts: IApiCallOptions = {},
    ) {
        if (opts?.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        const endpoint = `${aqe.defaults.publicApiDomain}/v2/users/${id}/permissions/accounts`

        let createdRecords: AccountUserModel[] = []
        let updatedRecords: AccountUserModel[] = []
        const createSets = changeRecords.filter((set) => !set.clean || !set.dirty.getId())
        const updateSets = changeRecords.filter((set) => set.clean && set.dirty.getId())

        if (createSets.length) {
            try {
                const res = await axios.post(
                    endpoint,
                    createSets.map((set) => set.dirty.serialize()),
                )
                if (res.data.data) {
                    createdRecords = res.data.data.map(AccountUserModel.fromCached)
                }
            } catch (err) {
                throw err
            }
        }

        if (updateSets.length) {
            try {
                const res = await axios.patch(
                    endpoint,
                    updateSets.map((set) => set.dirty.serialize()),
                )
                if (res.data.data) {
                    updatedRecords = res.data.data.map(AccountUserModel.fromCached)
                }
            } catch (err) {
                throw err
            }
        }

        if (opts?.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return [...createdRecords, ...updatedRecords]
    }

    public async updateDomainUserPermissions(
        id: number,
        changeRecords: Array<IPermissionsChangeSet<DomainUserModel>>,
        opts: IApiCallOptions = {},
    ) {
        if (opts?.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        const endpoint = `${aqe.defaults.publicApiDomain}/v2/users/${id}/permissions/domains`

        let createdRecords: DomainUserModel[] = []
        let updatedRecords: DomainUserModel[] = []
        const createSets = changeRecords.filter((set) => !set.clean || !set.dirty.getId())
        const updateSets = changeRecords.filter((set) => set.clean && set.dirty.getId())

        if (createSets.length) {
            try {
                const res = await axios.post(
                    endpoint,
                    createSets.map((set) => set.dirty.serialize()),
                )
                if (res.data.data) {
                    createdRecords = res.data.data.map(DomainUserModel.fromCached)
                }
            } catch (err) {
                throw err
            }
        }

        if (updateSets.length) {
            try {
                const res = await axios.patch(
                    endpoint,
                    updateSets.map((set) => set.dirty.serialize()),
                )
                if (res.data.data) {
                    updatedRecords = res.data.data.map(DomainUserModel.fromCached)
                }
            } catch (err) {
                throw err
            }
        }

        if (opts?.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return [...createdRecords, ...updatedRecords]
    }

    public async resetAccountAccess(
        userId: number,
        accountId: number,
        dryRun: boolean,
    ): Promise<AccessResetResult<IRemovedAccountAccess>> {
        return this.resetAccess<IRemovedAccountAccess>(`accounts/${accountId}`, userId, dryRun)
    }

    public async resetDomainAccess(
        userId: number,
        domainId: number,
        dryRun: boolean,
    ): Promise<AccessResetResult<{ domain: IRemovedDomainRow }>> {
        return this.resetAccess<{ domain: IRemovedDomainRow }>(`domains/${domainId}`, userId, dryRun)
    }

    protected async resetAccess<T>(path: string, userId: number, dryRun: boolean): Promise<AccessResetResult<T>> {
        const query = dryRun ? '?dry_run=true' : ''

        try {
            const res = await axios.delete(
                `${aqe.defaults.publicApiDomain}/v3/users/${userId}/permissions/${path}${query}`,
            )

            return { ok: true, removed: res.data?.data?.removed, cacheStale: false }
        } catch (err) {
            if (process.env.IS_LOCAL) {
                console.debug('reset_access_error', err)
            }

            const status = err?.response?.status
            if (status === 503 && err.response.data?.error_type === 'ACCESS_CACHE_STALE') {
                return { ok: true, cacheStale: true }
            }
            if (status === 409 && err.response.data?.error_type === 'NOT_NETWORK_SCOPE') {
                return { ok: false, failure: 'not_network_scope' }
            }
            if (status === 403) {
                return { ok: false, failure: 'refused' }
            }

            return { ok: false, failure: 'failed' }
        }
    }

    public async fetchAccessPolicies(
        types: string[],
        opts: ApiCallOptions = {},
    ): Promise<IApiResponse<IAccessPolicy[]>> {
        let ok = false
        let data: any[] = []
        let meta: any = {}
        let cancelled = false
        let error

        if (opts.showLoadingScreen) {
            this.appService.setModuleLoading()
        }
        const options = querystring.stringify({ types } || {})

        try {
            const res = await axiosFetch(
                'get',
                {
                    url: `${aqe.defaults.publicApiDomain}/v3/users/access-policies?${options}`,
                },
                opts.cancellationKey,
            )

            const { data: policies, ...resMeta } = res.data

            ok = true
            data = policies
            meta = resMeta
        } catch (err) {
            error = err

            if (isAxiosCancellation(err)) {
                cancelled = true
            } else if (isAxiosResponseError(err)) {
                simpleNotification('error', err.response.data.message)
            }
        }

        if (opts.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return { ok, data, meta, cancelled, error }
    }

    public async fetchUserAbilities(
        userId: number,
        etag?: string | null,
    ): Promise<{ rules: SerializedRule[]; etag: string; notModified: boolean }> {
        const headers: Record<string, string> = {}
        if (etag) {
            headers['If-None-Match'] = etag
        }

        try {
            const res = await axios.get(`${aqe.defaults.publicApiDomain}/v3/users/${userId}/abilities`, {
                headers,
                validateStatus: (status) => status === 200 || status === 304,
            })

            if (res.status === 304) {
                return { rules: [], etag: etag!, notModified: true }
            }

            return {
                rules: res.data.data,
                etag: res.headers['etag'] ?? '',
                notModified: false,
            }
        } catch (err) {
            if (process.env.IS_LOCAL) {
                console.debug('fetch_user_abilities_error', err)
            }

            throw err
        }
    }

    public async fetchUserRoleAccessPolicies(
        userId: number,
        opts: ApiCallOptions = {},
    ): Promise<IApiResponse<Array<IAccessPolicy & { user_role_ids: number[] }>>> {
        let ok = false
        let data: any[] = []
        let meta: any = {}
        let cancelled = false
        let error

        if (opts.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            const res = await axiosFetch(
                'get',
                {
                    url: `${aqe.defaults.publicApiDomain}/v3/users/${userId}/user-role-access-policies`,
                },
                opts.cancellationKey,
            )

            const { data: userRolePolicies, ...resMeta } = res.data

            ok = true
            data = userRolePolicies
            meta = resMeta
        } catch (err) {
            error = err

            if (isAxiosCancellation(err)) {
                cancelled = true
            } else if (isAxiosResponseError(err)) {
                simpleNotification('error', err.response.data.message)
            }
        }

        if (opts.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return { ok, data, meta, cancelled, error }
    }
}

export default PermissionsService
